// Shared by the client's CustomerCounterOfferModal and the customer's ProposalsTab,
// so both read and estimate customer rates alike.

export const fmt = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? "0.00" : n.toFixed(2);
};

export const toNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
};

export const PAYMENT_TYPE_LABELS = {
  HOURLY: "Hourly",
  HALF_DAY: "Half Day",
  FULL_DAY: "Full Day",
  FIXED: "Fixed",
  FTH: "First two hours",
  CUSTOM_HOURS: "Fixed Hours",
};

// The customer-rate fields a client can counter, per payment type. Mirrors what
// CustomerRates shows: for the day/block types `hourlyRate` is the additional
// hourly rate charged past the block.
// Hours worked outside business hours, on any payment type. Optional: a work order
// may have none, and a counter offer may leave it as it is.
const NON_BUSINESS_HOUR_RATE_FIELD = {
  name: "nonBusinessHourRate",
  label: "Non-Business Hour Rate",
  isAmount: true,
  optional: true,
};

export const RATE_FIELDS = {
  HOURLY: [
    { name: "hourlyRate", label: "Hourly Rate", isAmount: true },
    { name: "minimumHours", label: "Minimum Payable Hours" },
    { name: "additionalHourlyRate", label: "Additional Hourly rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
  FIXED: [
    { name: "fixedAmount", label: "Fixed Rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
  HALF_DAY: [
    { name: "halfDayRate", label: "Half Day Rate", isAmount: true },
    { name: "hourlyRate", label: "Additional Hourly rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
  FULL_DAY: [
    { name: "fullDayRate", label: "Full Day Rate", isAmount: true },
    { name: "hourlyRate", label: "Additional Hourly rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
  FTH: [
    { name: "fthRate", label: "First Two Hours Rate", isAmount: true },
    { name: "hourlyRate", label: "Additional Hourly rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
  CUSTOM_HOURS: [
    { name: "customHours", label: "Hours" },
    { name: "customHoursRate", label: "Hours Rate", isAmount: true },
    { name: "hourlyRate", label: "Additional Hourly rate", isAmount: true },
    NON_BUSINESS_HOUR_RATE_FIELD,
  ],
};

// An optional rate is only listed when one of the given values actually has it.
export const isRateShown = (field, ...values) =>
  !field.optional || values.some((value) => toNum(value) > 0);

const isBlank = (value) => value === "" || value === null || value === undefined;

// What a labour input holds: a required rate must be above 0; an optional one may
// be left empty (meaning "keep the current rate") or set to 0 or more.
export const isRateInputValid = (field, value) =>
  field.optional ? isBlank(value) || toNum(value) >= 0 : toNum(value) > 0;

// Whether an input changes the rate: an empty optional input keeps the current one.
export const isRateInputChanged = (field, value, current) =>
  !(field.optional && isBlank(value)) && toNum(value) !== toNum(current);

// On a customer counter offer the proposed rate for `hourlyRate` is stored as
// `customerHourlyRate`, and so on; the unprefixed field is the work order's rate
// when the offer was sent.
export const toCustomerRateField = (name) =>
  `customer${name.charAt(0).toUpperCase()}${name.slice(1)}`;

// Up-front labour for the estimate: what the customer pays before any
// additional hours, per payment type.
export const estimateLabour = (paymentType, rates) => {
  switch (paymentType) {
    case "HOURLY":
      return (toNum(rates?.hourlyRate) || 0) * Math.max(toNum(rates?.minimumHours) || 0, 1);
    case "FIXED":
      return toNum(rates?.fixedAmount) || 0;
    case "HALF_DAY":
      return toNum(rates?.halfDayRate) || 0;
    case "FULL_DAY":
      return toNum(rates?.fullDayRate) || 0;
    case "FTH":
      return toNum(rates?.fthRate) || 0;
    case "CUSTOM_HOURS":
      return toNum(rates?.customHoursRate) || 0;
    default:
      return 0;
  }
};
